import { difference, union } from "lodash";
import {
  type Identifier,
  RecordContextProvider,
  RecordRepresentation,
  useListContext,
  useLocaleState,
  useTimeout,
  useTranslate,
} from "ra-core";
import { type MouseEvent, useCallback, useRef } from "react";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Status } from "../misc/Status";
import { formatRelativeDate } from "../misc/RelativeDate";
import type { Contact } from "../types";
import { Avatar } from "./Avatar";
import {
  getLeadSlaLevel,
  computeLeadScore,
  leadScoreBadgeClass,
  isContactQualifying,
  type LeadSlaLevel,
} from "./contactModel";
import { TagsList } from "./TagsList";
import { useNeedsWhatsAppReply } from "./useDeskAttention";

const slaBadgeClass: Record<LeadSlaLevel, string> = {
  ok: "bg-emerald-100 text-emerald-800 border-transparent",
  warn: "bg-amber-100 text-amber-900 border-transparent",
  overdue: "bg-red-100 text-red-800 border-transparent",
};

export const ContactListContent = () => {
  const translate = useTranslate();
  const { contactIds: needsReplyContactIds } = useNeedsWhatsAppReply();
  const {
    data: contacts,
    error,
    isPending,
    onToggleItem,
    onSelect,
    selectedIds,
  } = useListContext<Contact>();
  const lastSelected = useRef<Identifier | null>(null);

  // Handle shift+click to select a range of rows
  const handleToggleItem = useCallback(
    (id: Identifier, event: MouseEvent) => {
      if (!contacts) return;

      const ids = contacts.map((contact) => contact.id);
      const lastSelectedIndex = lastSelected.current
        ? ids.indexOf(lastSelected.current)
        : -1;

      if (event.shiftKey && lastSelectedIndex !== -1) {
        const index = ids.indexOf(id);
        const idsBetweenSelections = ids.slice(
          Math.min(lastSelectedIndex, index),
          Math.max(lastSelectedIndex, index) + 1,
        );

        const isClickedItemSelected = selectedIds?.includes(id);
        const newSelectedIds = isClickedItemSelected
          ? difference(selectedIds, idsBetweenSelections)
          : union(selectedIds, idsBetweenSelections);

        onSelect?.(newSelectedIds);
      } else {
        onToggleItem(id);
      }

      lastSelected.current = id;
    },
    [contacts, selectedIds, onSelect, onToggleItem],
  );

  if (isPending) {
    return <Skeleton className="w-full h-9" />;
  }

  if (error) {
    return null;
  }

  return (
    <div className="md:divide-y">
      {contacts.map((contact) => (
        <RecordContextProvider key={contact.id} value={contact}>
          <ContactItemContent
            contact={contact}
            handleToggleItem={handleToggleItem}
            needsWhatsAppReply={needsReplyContactIds.has(String(contact.id))}
          />
        </RecordContextProvider>
      ))}

      {contacts.length === 0 && (
        <div className="p-4">
          <div className="text-muted-foreground">
            {translate("resources.contacts.empty.title", {})}
          </div>
        </div>
      )}
    </div>
  );
};

const ContactItemContent = ({
  contact,
  handleToggleItem,
  needsWhatsAppReply = false,
}: {
  contact: Contact;
  handleToggleItem: (id: Identifier, event: MouseEvent) => void;
  needsWhatsAppReply?: boolean;
}) => {
  const translate = useTranslate();
  const [locale = "en"] = useLocaleState();
  const { selectedIds } = useListContext<Contact>();
  const lastActivity = contact.last_seen
    ? formatRelativeDate(contact.last_seen, locale)
    : null;
  const sla = getLeadSlaLevel(contact);
  const leadScore = computeLeadScore({
    contact,
    timeline: [],
    needsWhatsAppReply,
  });

  return (
    <div className="flex flex-row items-center pl-2 pr-4 py-2 hover:bg-muted transition-colors first:rounded-t-xl last:rounded-b-xl">
      <div
        className="px-4 py-3 flex items-center cursor-pointer"
        onClick={(e) => handleToggleItem(contact.id, e)}
      >
        <Checkbox
          className="cursor-pointer"
          checked={selectedIds.includes(contact.id)}
        />
      </div>
      <Link
        to={`/contacts/${contact.id}/show`}
        className="flex-1 flex flex-row gap-4 items-center"
      >
        <Avatar />
        <div className="flex-1 min-w-0">
          <div className="font-medium flex items-center gap-2 flex-wrap">
            <span>{`${contact.first_name} ${contact.last_name ?? ""}`}</span>
            {sla && (
              <Badge
                variant="secondary"
                className={`text-[10px] px-1.5 py-0 font-medium ${slaBadgeClass[sla]}`}
              >
                {translate(`resources.contacts.sla.${sla}`)}
              </Badge>
            )}
            {contact.sales_id == null && (
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 font-medium"
              >
                {translate("resources.contacts.filters.unassigned")}
              </Badge>
            )}
            {needsWhatsAppReply && (
              <Badge
                variant="secondary"
                className="text-[10px] px-1.5 py-0 font-medium bg-emerald-100 text-emerald-800 border-transparent"
              >
                {translate("resources.contacts.filters.needs_reply")}
              </Badge>
            )}
            {isContactQualifying(contact) && (
              <Badge
                variant="secondary"
                className="text-[10px] px-1.5 py-0 font-medium bg-sky-100 text-sky-800 border-transparent"
              >
                {translate("resources.contacts.qualify.qualifying", {
                  _: "Qualifying…",
                })}
              </Badge>
            )}
            <Badge
              variant="secondary"
              className={`text-[10px] px-1.5 py-0 font-medium ${leadScoreBadgeClass(leadScore.tier)}`}
              title={translate("resources.contacts.assist.lead_score")}
            >
              {translate(`resources.contacts.assist.score_${leadScore.tier}`, {
                _: leadScore.tier,
              })}{" "}
              {leadScore.score}
            </Badge>
          </div>
          {contact.title || contact.company_id != null || contact.nb_tasks ? (
            <div className="text-sm text-muted-foreground">
              {contact.title && contact.company_id != null
                ? `${translate("resources.contacts.position_at", {
                    title: contact.title,
                  })} `
                : contact.title}
              {contact.company_id != null && (
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  link={false}
                >
                  <TextField source="name" />
                </ReferenceField>
              )}
              {contact.nb_tasks
                ? ` - ${translate("crm.common.task_count", {
                    smart_count: contact.nb_tasks,
                  })}`
                : ""}
              &nbsp;&nbsp;
              <TagsList />
            </div>
          ) : null}
        </div>
        {contact.last_seen && (
          <div className="text-right ml-4">
            <div
              className="text-sm text-muted-foreground"
              title={contact.last_seen}
            >
              {translate("crm.common.last_activity_with_date", {
                date: lastActivity,
              })}{" "}
              <Status status={contact.status} />
            </div>
          </div>
        )}
      </Link>
    </div>
  );
};

export const ContactListContentMobile = () => {
  const translate = useTranslate();
  const { contactIds: needsReplyContactIds } = useNeedsWhatsAppReply();
  const {
    data: contacts,
    error,
    isPending,
    refetch,
  } = useListContext<Contact>();
  const oneSecondHasPassed = useTimeout(1000);

  if (isPending) {
    if (!oneSecondHasPassed) {
      return null;
    }
    return (
      <>
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="flex flex-row items-center py-2 hover:bg-muted transition-colors first:rounded-t-xl last:rounded-b-xl"
          >
            <div className="flex flex-row gap-4 items-center mr-4">
              <Skeleton className="w-10 h-10 rounded-full" />
            </div>
            <div className="flex-1 min-w-0">
              <Skeleton className="w-32 h-5 mb-2" />
              <Skeleton className="w-48 h-4" />
            </div>
          </div>
        ))}
      </>
    );
  }

  if (error && !contacts) {
    return (
      <div className="p-4">
        <div className="text-center text-muted-foreground mb-4">
          {translate("resources.contacts.list.error_loading")}
        </div>
        <div className="text-center mt-2">
          <Button
            onClick={() => {
              refetch();
            }}
          >
            <RotateCcw />
            {translate("crm.common.retry")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="md:divide-y">
      {contacts.map((contact) => (
        <RecordContextProvider key={contact.id} value={contact}>
          <ContactItemContentMobile
            contact={contact}
            needsWhatsAppReply={needsReplyContactIds.has(String(contact.id))}
          />
        </RecordContextProvider>
      ))}
      {contacts.length === 0 && (
        <div className="p-4">
          <div className="text-muted-foreground">
            {translate("resources.contacts.empty.title")}
          </div>
        </div>
      )}
    </div>
  );
};

const ContactItemContentMobile = ({
  contact,
  needsWhatsAppReply = false,
}: {
  contact: Contact;
  needsWhatsAppReply?: boolean;
}) => {
  const translate = useTranslate();
  const sla = getLeadSlaLevel(contact);
  const leadScore = computeLeadScore({
    contact,
    timeline: [],
    needsWhatsAppReply,
  });
  return (
    <Link
      to={`/contacts/${contact.id}/show`}
      className="flex flex-row gap-4 items-center py-2 hover:bg-muted transition-colors"
    >
      <Avatar />
      <div className="flex flex-col grow justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex justify-between gap-2">
            <div className="font-medium flex items-center gap-1.5 flex-wrap min-w-0">
              <RecordRepresentation />
              {sla && (
                <Badge
                  variant="secondary"
                  className={`text-[10px] px-1.5 py-0 font-medium ${slaBadgeClass[sla]}`}
                >
                  {translate(`resources.contacts.sla.${sla}`)}
                </Badge>
              )}
              {needsWhatsAppReply && (
                <Badge
                  variant="secondary"
                  className="text-[10px] px-1.5 py-0 font-medium bg-emerald-100 text-emerald-800 border-transparent"
                >
                  {translate("resources.contacts.filters.needs_reply")}
                </Badge>
              )}
              {isContactQualifying(contact) && (
                <Badge
                  variant="secondary"
                  className="text-[10px] px-1.5 py-0 font-medium bg-sky-100 text-sky-800 border-transparent"
                >
                  {translate("resources.contacts.qualify.qualifying", {
                    _: "Qualifying…",
                  })}
                </Badge>
              )}
              <Badge
                variant="secondary"
                className={`text-[10px] px-1.5 py-0 font-medium ${leadScoreBadgeClass(leadScore.tier)}`}
                title={translate("resources.contacts.assist.lead_score")}
              >
                {translate(
                  `resources.contacts.assist.score_${leadScore.tier}`,
                  {
                    _: leadScore.tier,
                  },
                )}{" "}
                {leadScore.score}
              </Badge>
            </div>
            <Status status={contact.status} />
          </div>
          <div className="text-sm text-muted-foreground">
            <div className="flex flex-col gap-1">
              <span>
                {contact.title && contact.company_id != null
                  ? `${translate("resources.contacts.position_at", {
                      title: contact.title,
                    })} `
                  : contact.title}
                {contact.company_id != null && (
                  <ReferenceField
                    source="company_id"
                    reference="companies"
                    link={false}
                  >
                    <TextField source="name" />
                  </ReferenceField>
                )}
              </span>
              {contact.nb_tasks ? (
                <span>
                  {translate("crm.common.task_count", {
                    smart_count: contact.nb_tasks,
                  })}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};
